import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import OperatoryGrid from '../components/OperatoryGrid';
import AlertStack from '../components/AlertStack';
import Sidebar from '../components/Sidebar';
import FloatingButtonIsland from '../components/FloatingButtonIsland';
import { Activity, Shield, UserCheck, Check } from 'lucide-react';
import { toast } from 'sonner';

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const { practice } = usePractice();
  const { darkMode } = useTheme();
  const { socket, connected } = useSocket();
  const [operatories, setOperatories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Assistant provider selection
  const isAssistant = user?.role === 'assistant' || user?.base_role === 'assistant';
  const [showProviderPrompt, setShowProviderPrompt] = useState(false);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState(() => {
    try {
      const saved = sessionStorage.getItem('assistant_selected_providers');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Prompt assistant to pick providers on first load if none selected
  useEffect(() => {
    if (isAssistant && !sessionStorage.getItem('assistant_selected_providers')) {
      axios.get(`${API}/providers`).then(res => {
        setAvailableProviders(res.data);
        setShowProviderPrompt(true);
      }).catch(() => {});
    }
  }, [isAssistant]);

  const toggleProvider = (id) => {
    setSelectedProviderIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      return next;
    });
  };

  const confirmProviders = () => {
    if (selectedProviderIds.length === 0) {
      toast.error('Please select at least one provider');
      return;
    }
    sessionStorage.setItem('assistant_selected_providers', JSON.stringify(selectedProviderIds));
    setShowProviderPrompt(false);
  };

  const fetchData = useCallback(async () => {
    try {
      const operatoriesRes = await axios.get(`${API}/operatories`);
      setOperatories(operatoriesRes.data);
      // Users list is only accessible to admins - fetch separately
      try {
        const usersRes = await axios.get(`${API}/users`);
        setAllUsers(usersRes.data);
      } catch {
        // Non-admin users can't access user list - that's fine
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Listen for real-time updates via WebSocket
    if (socket) {
      const handleUpdate = () => fetchData();
      socket.on('operatory_updated', handleUpdate);
      socket.on('alert_created', handleUpdate);
      socket.on('custom_button_triggered', handleUpdate);
      socket.on('custom_button_cleared', handleUpdate);
      return () => {
        socket.off('operatory_updated', handleUpdate);
        socket.off('alert_created', handleUpdate);
        socket.off('custom_button_triggered', handleUpdate);
        socket.off('custom_button_cleared', handleUpdate);
      };
    }
    // Fallback polling when socket is not connected
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData, socket]);

  // Get user's display name with disambiguation if needed
  const getDisplayName = () => {
    if (!user) return '';
    
    // Get user's first name
    let firstName = user.first_name || '';
    let lastName = user.last_name || '';
    
    if (!firstName && user.full_name) {
      const parts = user.full_name.split(' ');
      firstName = parts[0] || '';
      lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    }
    
    if (!firstName) return user.full_name || 'User';
    
    // Check if multiple users share the same first name
    const firstNameLower = firstName.toLowerCase();
    let duplicateCount = 0;
    
    for (let i = 0; i < allUsers.length; i++) {
      const u = allUsers[i];
      let uFirstName = u.first_name || '';
      if (!uFirstName && u.full_name) {
        const parts = u.full_name.split(' ');
        uFirstName = parts[0] || '';
      }
      if (uFirstName.toLowerCase() === firstNameLower) {
        duplicateCount++;
      }
    }
    
    // If multiple users have same first name, add last initial
    if (duplicateCount > 1 && lastName) {
      return firstName + ' ' + lastName[0].toUpperCase() + '.';
    }
    
    return firstName;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin text-dp-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Apply custom background from practice settings
  const bgStyle = darkMode ? {
    backgroundColor: '#0f172a',
    backgroundImage: practice?.background_image_dark ? `url(${practice.background_image_dark})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  } : {
    backgroundColor: practice?.background_color || '#f8fafc',
    backgroundImage: practice?.background_image ? `url(${practice.background_image})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
      <Sidebar />
      
      {/* Header */}
      <div className="pl-28 pr-8 pt-6 pb-4">
        <div className="glass-panel rounded-2xl p-6 flex items-center justify-between">
          {/* Left side - Practice Logo (if uploaded) above Welcome message */}
          <div className="flex-1">
            {practice?.logo_image && (
              <div className="mb-3">
                <img 
                  src={practice.logo_image} 
                  alt={`${practice.name} logo`}
                  className="max-h-16 max-w-[200px] object-contain"
                  data-testid="practice-logo"
                />
              </div>
            )}
            <p className="text-slate-600 dark:text-slate-400">
              Welcome back, {getDisplayName()}
              {(user?.role === 'admin' || user?.is_admin) && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                  <Shield className="w-3.5 h-3.5" />
                </span>
              )}
            </p>
          </div>
          {/* Right side - DentalPulse Logo (always shown) */}
          <div className="flex-shrink-0 ml-4">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-14 w-auto object-contain"
              data-testid="dentalpulse-logo"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-28 pr-8 pb-24">
        <OperatoryGrid operatories={operatories} onUpdate={fetchData} showAll={practice?.dashboard_show_all || false} />
      </div>

      {/* Floating Button Island */}
      <FloatingButtonIsland />

      {/* Alert Stack */}
      <AlertStack onStatusChange={fetchData} />

      {/* Assistant Provider Selection Modal */}
      {showProviderPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" data-testid="provider-prompt-overlay">
          <div className={`rounded-2xl p-6 w-full max-w-md shadow-2xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-800'}`} data-testid="provider-prompt-modal">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-dp-500" />
              <h2 className="text-lg font-bold">Who are you assisting today?</h2>
            </div>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Select the provider(s) you are working with. This filters your widget and alerts.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableProviders.map(p => {
                const selected = selectedProviderIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    data-testid={`provider-prompt-${p.id}`}
                    onClick={() => toggleProvider(p.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      selected
                        ? 'bg-dp-100 border-2 border-dp-500 dark:bg-dp-900/40 dark:border-dp-400'
                        : `border-2 ${darkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300'}`
                    }`}
                  >
                    <span className="font-medium text-sm">{p.full_name}</span>
                    {selected && <Check className="w-5 h-5 text-dp-500" />}
                  </button>
                );
              })}
            </div>
            <button
              data-testid="provider-prompt-confirm"
              onClick={confirmProviders}
              className="mt-4 w-full py-3 rounded-xl bg-dp-600 hover:bg-dp-500 text-white font-semibold transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}